import { Link } from '@tanstack/react-router';
import { format } from 'date-fns';
import { CircleArrowLeft, CloudUpload } from 'lucide-react';
import { toast } from 'sonner';

import { useGetEvent } from '~entities/events';
import { useGetEventPaymentHistory } from '~entities/events/api/get-event-payment-history';

import { EventPaymentHistoryTable } from '~features/events/ui/event-payment-history-table';

import { EVENT_PAYMENT_CSV_HEADERS, exportToCSV } from '~shared/helpers/csv';
import { formatDateOnlySafe } from '~shared/helpers/datetime';
import { Button } from '~shared/shadcn/ui/button';

interface EventPaymentHistoryPageProps {
  eventId: string;
}

export const EventPaymentHistoryPage = ({
  eventId,
}: EventPaymentHistoryPageProps) => {
  const { data: eventData } = useGetEvent({ eventId });
  const { data: paymentRows, isFetching } = useGetEventPaymentHistory({
    eventId,
  });

  const handleExport = () => {
    if (!paymentRows || paymentRows.length === 0) {
      toast.error('No event payment data available to export.');
      return;
    }

    const headers = Object.values(EVENT_PAYMENT_CSV_HEADERS);
    const formattedData = paymentRows.map((row) => ({
      [EVENT_PAYMENT_CSV_HEADERS.STUDENT_NAME]: row.studentName,
      [EVENT_PAYMENT_CSV_HEADERS.PAYMENT_STATUS]: row.paymentStatus,
      [EVENT_PAYMENT_CSV_HEADERS.ENROLLMENT_STATUS]: row.enrollmentStatus,
      [EVENT_PAYMENT_CSV_HEADERS.ENROLLMENT_DATE]: row.enrollmentDate
        ? formatDateOnlySafe(row.enrollmentDate, 'dd-MM-yyyy')
        : '',
      [EVENT_PAYMENT_CSV_HEADERS.DUE_DATE]: row.dueDate
        ? formatDateOnlySafe(row.dueDate, 'dd-MM-yyyy')
        : '',
      [EVENT_PAYMENT_CSV_HEADERS.TOTAL_AMOUNT]: row.totalAmount ?? '',
      [EVENT_PAYMENT_CSV_HEADERS.AMOUNT_PAID]: row.amount ?? '',
      [EVENT_PAYMENT_CSV_HEADERS.PAYMENT_INTENT_ID]: row.paymentIntentId ?? '',
      [EVENT_PAYMENT_CSV_HEADERS.PROBLEM]: row.problem,
    }));

    const eventName = eventData?.title ?? 'event';
    exportToCSV(
      formattedData,
      headers,
      `${eventName}-payment-report-${format(new Date(), 'yyyy-MM-dd-HH-mm-ss')}.csv`,
    );
  };

  return (
    <div className='p-6'>
      <div className='mb-6 flex items-center justify-between gap-4'>
        <Link
          to='/events/$eventId/enrolled-students'
          params={{ eventId }}
          className='flex items-center gap-2'
        >
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>
            {eventData?.title ?? 'Event'}
          </h2>
        </Link>

        <Button
          variant='outline'
          onClick={handleExport}
          disabled={isFetching || !paymentRows || paymentRows.length === 0}
        >
          <CloudUpload className='mr-2 size-4' />
          Export
        </Button>
      </div>

      <EventPaymentHistoryTable eventId={eventId} />
    </div>
  );
};
